import * as React from 'react';
import { Permission, PromptConfirmationModal, DisplayCodes } from 'ui';
import { ButtonTertiary } from 'ui/common/button/button-tertiary';
import { MdEdit } from 'react-icons/md';
import styles from './code-title-tile.scss';
import { TileProps, TileState } from 'ui/tile/tile';
import { MeatballMenu, ValidationLevel } from 'ui/common';
import { DisplayTextWithLinks } from 'ui/common/display-text-with-links';
import { DetailsModal } from 'ui/common/details-modal/details-modal';
import { debounceRender } from 'ui/common/debounce-render';
import { OpenStateInjectedProps, withOpenState } from 'ui/common/enhancers';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { ModalSize } from 'app/components/constants';

interface CodeTitleTileProps {
  showEditFooterDetailsButton?: boolean;
  textEditFooterDetails?: string;
  canEditFooterDetails?: boolean;
  hideTileDetails?: boolean;
}

export class CodeTitleTileComponent extends React.PureComponent<CodeTitleTileProps & TileProps & OpenStateInjectedProps, any> {

  public static defaultProps: Partial<CodeTitleTileProps & TileProps> = {
    enabledChildEntityOperations: { remove: false }
  };

  public state: Readonly<TileState> = {
    hideImmediately: true
  };
  
  private handleSelect = (e?: any) => {
    e && e.preventDefault();

    this.props.onSelectDocumentItem && this.props.onSelectDocumentItem();
    this.setState({hideImmediately: false} );
    this.props.open();
  };

  private handleDeselect = (e?: any) => {
    e && e.preventDefault();

    if (this.props.activate) {
      this.setState({hideImmediately: true} );
    }
    this.props.close();
  };

  private handleDelete = (e?: any) => {
    e && e.preventDefault();
    this.props.onDeleteDocumentItem && this.props.onDeleteDocumentItem();
  };

  private renderViewDetailsButton = () => {
    const {textShowDetails} = this.props;
    const childrenHasErrors = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.error);

    const childrenHasWarnings = !!this.props.validationDescendants?.length && 
      this.props.validationDescendants.some(v => v.level === ValidationLevel.warning);

    const validationClass = childrenHasErrors ?
      styles.hasChildErrors : (childrenHasWarnings ? styles.hasChildWarnings : '');
  
    return <>
      <div className={`${styles.buttonContainer}`} onClick={this.handleSelect}>
        <ButtonTertiary classNames={`${styles.detailsButton} ${validationClass}`} onClick={this.handleSelect}>
          {textShowDetails}
        </ButtonTertiary>
      </div>
    </>;
  };

  private renderContextMenu() {

    if  (!this.shouldRenderContextMenu()) {
      return undefined;
    }

    const { enabledChildEntityOperations, textDelete, textDeleteMessage, textContinue, textCancel } = this.props;

    return (
      <Permission requireAny="remove" permissions={enabledChildEntityOperations}>
        <div className={styles.tileDropdown}>
          <MeatballMenu>
              <Permission requireAll="remove" permissions={enabledChildEntityOperations} isListElement={true}>
                <PromptConfirmationModal
                  title={textDelete}
                  description={textDeleteMessage}
                  continueText={textContinue}
                  cancelText={textCancel}
                  onContinue={this.handleDelete}>
                  <button type="button" className="dropdown-item">{textDelete}</button>
                </PromptConfirmationModal>
              </Permission>
          </MeatballMenu>
        </div>
      </Permission>
    );
  }

  public render() {
    const {
      componentId, primaryKey, details, children, activate,
      showPrimaryKey = true,
      showPrimaryKeyLongDescription = true,
      linkifyLongDescription = true,
      showDetailsButton = true,
      showEditFooterDetailsButton = true,
      textEditFooterDetails,
      textDone, className, expandAll, canEditFooterDetails,
      hideTileDetails = false
    } = this.props;
    const { codeTitleTile, codeTitleTileCard, header, displayCodes, main } = styles;

    const renderAsOpened = showDetailsButton && activate && details && !this.state.hideImmediately;
    const renderHeader = showPrimaryKey || this.shouldRenderContextMenu();
    const activeStyling = renderAsOpened ? styles.activeTile : '';

    const detailsButton = showDetailsButton && details && this.renderViewDetailsButton();
    const withShowDetailsButtonStyle = showDetailsButton && details && !expandAll && styles.withShowDetailsButton;

    return (
      <>
        {renderAsOpened ? <div tabIndex={0} onFocus={this.handleDeselect}/> : ''}
        <div className={`${className} ${codeTitleTile} ${activeStyling}`}>
          <div id={componentId} className={`${codeTitleTileCard} ${withShowDetailsButtonStyle}`}>
            {renderHeader && <div className={header}>
              <div className={styles.btnIconsWrapper}>
                {!expandAll && detailsButton}
                {this.renderContextMenu()}
              </div>
              {showPrimaryKey && <h3>
                <DisplayCodes
                  componentId={`${componentId}-code`}
                  className={displayCodes}
                  valueItems={[primaryKey]}
                  displayIcon={'left'}
                  options={undefined}
                  onChange={undefined}
                />
              </h3>}
              {
                showPrimaryKey && showPrimaryKeyLongDescription && primaryKey.longDescription &&
                  <DisplayTextWithLinks 
                    className={styles.longDescription} 
                    linkify={linkifyLongDescription} 
                    text={primaryKey.longDescription} />
              }
            </div>}
            {children && (<div className={`${main} row`}>
              {children}
              {
                showEditFooterDetailsButton && details &&
                  <DetailsModal 
                    className={`${styles.editFooterDetailsButtonWrapper} ${canEditFooterDetails ? styles.hideEditFooterIcon : ''}`} 
                    editDetailsButton={<MdEdit className={styles.editFooterDetailsButton}/>} tooltip={textEditFooterDetails} 
                    textDone={textDone} textHeaderTitle={primaryKey?.description}>
                    {details}
                  </DetailsModal>
              }
            </div>)}
            {expandAll && details && !hideTileDetails && <div className="col-12">{details}</div>}

          </div>

          {renderAsOpened && <ModalWrapper headerTitle={showPrimaryKey ? primaryKey.description : undefined}
                                onClose={this.handleDeselect} textClose={textDone} onDone={this.handleDeselect}
                                btnTitle={textDone} size={ModalSize.xtraLarge}>
                                {details}
                             </ModalWrapper>}
        </div>
      </>
    );
  }

  private shouldRenderContextMenu = () => {
    const { enabledChildEntityOperations } = this.props;

    return enabledChildEntityOperations && Object.keys(enabledChildEntityOperations)
      .reduce((acc, key) => acc || enabledChildEntityOperations[key], false);
  };
}

const t = withOpenState()(debounceRender(CodeTitleTileComponent));
export {t as CodeTitleTile};
